import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLConnection } from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Types ---

export interface CompanyRecentActivity {
  id: string;
  activityType: string;
  title: string;
  description: string;
  customerName: string;
  actorName: string;
  createdAt: string;
}

// --- GraphQL ---

interface ActivityNode {
  activityId: number;
  customerId: number;
  activityType: string;
  title: string;
  description: string;
  actorName: string;
  createdAt: string;
}

interface ActivityResponse {
  customers: {
    customerRecentActivityList: GraphQLConnection<ActivityNode>;
  };
}

const COMPANY_RECENT_ACTIVITY_QUERY = `
  query CompanyRecentActivity($first: Int, $sort: [CustomerRecentActivityListSortSortByInput!]) {
    customers {
      customerRecentActivityList(first: $first, sort: $sort) {
        edges {
          node {
            activityId
            customerId
            activityType
            title
            description
            actorName
            createdAt
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Mock data ---

function buildMockActivities(): CompanyRecentActivity[] {
  return [
    {
      id: 'mock-1',
      activityType: 'email',
      title: 'Payment reminder sent',
      description: 'Reminder for overdue invoices',
      customerName: 'Acme Corp',
      actorName: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    },
    {
      id: 'mock-2',
      activityType: 'phone',
      title: 'Follow-up call completed',
      description: 'Discussed payment schedule',
      customerName: 'Global Industries',
      actorName: 'Mike Chen',
      createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    },
    {
      id: 'mock-3',
      activityType: 'payment',
      title: 'Payment received',
      description: 'Partial payment allocated',
      customerName: 'Tech Solutions',
      actorName: 'System',
      createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    },
    {
      id: 'mock-4',
      activityType: 'note',
      title: 'Note added',
      description: 'Customer requested payment plan',
      customerName: 'Retail Partners',
      actorName: 'Emily Rodriguez',
      createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    },
    {
      id: 'mock-5',
      activityType: 'status_change',
      title: 'Case status changed',
      description: 'Escalated to senior collector',
      customerName: 'Summit Corp',
      actorName: 'System',
      createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    },
  ];
}

// --- Hook ---

export interface CompanyRecentActivitiesResult {
  activities: CompanyRecentActivity[];
  isLoading: boolean;
}

export function useCompanyRecentActivities(): CompanyRecentActivitiesResult {
  const apiConfig = useApiConfig();
  const mockData = buildMockActivities();

  const { data, isLoading } = useModeAwareQuery<CompanyRecentActivity[], Error>(
    ['company-recent-activities'],
    async ({ signal }) => {
      const response = await graphqlQuery<ActivityResponse>(
        apiConfig,
        COMPANY_RECENT_ACTIVITY_QUERY,
        {
          first: 10,
          sort: [{ field: 'CreatedAt', order: 'Descending' }],
        },
        signal,
      );

      return response.customers.customerRecentActivityList.edges.map((e) => ({
        id: String(e.node.activityId),
        activityType: e.node.activityType,
        title: e.node.title,
        description: e.node.description,
        customerName: '',
        actorName: e.node.actorName,
        createdAt: e.node.createdAt,
      }));
    },
    mockData,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  return { activities: data ?? [], isLoading };
}
